import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import { DescriptionOutlined } from '@mui/icons-material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
    getPatientPendingAnswers,
    getPatientQuestionnaireAnswers
} from '../../../../services/patient';
import OverviewCard from './OverviewCard';

const getPaginationCount = (response) => {
    const count = Number(response?.headers?.['pagination-count']);
    return Number.isFinite(count) ? count : response?.data?.length || 0;
};

const QuestionnairesOverviewCard = ({ patientUuid, onViewQuestionnaires }) => {
    const { t } = useTranslation();
    const answeredQuery = useQuery({
        queryKey: ['patient-questionnaires-summary', patientUuid, 'answered'],
        queryFn: () => getPatientQuestionnaireAnswers(patientUuid, 4, null),
        enabled: Boolean(patientUuid),
        staleTime: 60 * 1000,
        refetchOnMount: 'always'
    });
    const pendingQuery = useQuery({
        queryKey: ['patient-questionnaires-summary', patientUuid, 'pending'],
        queryFn: () => getPatientPendingAnswers(patientUuid, 3, null),
        enabled: Boolean(patientUuid),
        staleTime: 60 * 1000,
        refetchOnMount: 'always'
    });

    const answered = Array.isArray(answeredQuery.data?.data)
        ? answeredQuery.data.data
        : [];
    const pending = Array.isArray(pendingQuery.data?.data) ? pendingQuery.data.data : [];
    const pendingTotal = getPaginationCount(pendingQuery.data);
    const remainingPending = Math.max(pendingTotal - pending.length, 0);
    const isPending = answeredQuery.isPending || pendingQuery.isPending;
    const isError = answeredQuery.isError || pendingQuery.isError;

    return (
        <OverviewCard
            title={t('userPage.sections.questionnaires')}
            icon={<DescriptionOutlined fontSize="small" />}
            minHeight={230}
            contentInset={4}
        >
            {isPending ? (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: 1.5
                    }}
                >
                    <Skeleton variant="rounded" height={112} />
                    <Skeleton variant="rounded" height={112} />
                </Box>
            ) : isError ? (
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5
                    }}
                >
                    <Typography color="error">
                        {t('userPage.overview.questionnaires.loadError')}
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                            answeredQuery.refetch();
                            pendingQuery.refetch();
                        }}
                    >
                        {t('userPage.overview.measurements.retry')}
                    </Button>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: 1.5
                    }}
                >
                    <Box
                        sx={{
                            minWidth: 0,
                            height: 112,
                            p: 1.25,
                            border: '1px solid #D6DDE2',
                            borderRadius: 1.5,
                            bgcolor: '#FFFFFF',
                            boxShadow: '0 1px 3px rgba(38, 50, 56, 0.12)'
                        }}
                    >
                        <Typography
                            component="h3"
                            sx={{
                                color: 'primary.main',
                                fontSize: '0.8125rem',
                                fontWeight: 700,
                                lineHeight: 1.35,
                                mb: 0.5
                            }}
                        >
                            {t('userPage.overview.questionnaires.latest')}
                        </Typography>

                        {answered.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                {t('userPage.overview.questionnaires.noAnswered')}
                            </Typography>
                        ) : (
                            answered.map((answer) => (
                                <Box
                                    key={answer.uuid}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1.5,
                                        minHeight: 17
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        noWrap
                                        sx={{
                                            color: '#263238',
                                            fontWeight: 700,
                                            lineHeight: 1.35
                                        }}
                                    >
                                        {answer.questionnaire?.name || '—'}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            flexShrink: 0,
                                            color: 'text.secondary',
                                            fontWeight: 600,
                                            lineHeight: 1.35
                                        }}
                                    >
                                        {answer.ts && dayjs(answer.ts).isValid()
                                            ? dayjs(answer.ts).format('DD/MM/YYYY')
                                            : '—'}
                                    </Typography>
                                </Box>
                            ))
                        )}
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '84px minmax(0, 1fr)',
                            minWidth: 0,
                            height: 112,
                            p: 1.25,
                            border: '1px solid #D6DDE2',
                            borderRadius: 1.5,
                            bgcolor: '#FFFFFF',
                            boxShadow: '0 1px 3px rgba(38, 50, 56, 0.12)'
                        }}
                    >
                        <Typography
                            component="h3"
                            sx={{
                                gridColumn: '1 / -1',
                                color: 'primary.main',
                                fontSize: '0.8125rem',
                                fontWeight: 700,
                                lineHeight: 1.35,
                                mb: 0.5
                            }}
                        >
                            {t('userPage.overview.questionnaires.pending')}
                        </Typography>

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRight: '1px solid #D6DDE2'
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    color: '#263238',
                                    fontWeight: 700,
                                    lineHeight: 1.35
                                }}
                            >
                                {t('userPage.overview.questionnaires.total')}
                            </Typography>
                            <Typography
                                sx={{
                                    color: 'primary.main',
                                    fontSize: '1.5rem',
                                    fontWeight: 700,
                                    lineHeight: 1.2
                                }}
                            >
                                {pendingTotal}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                minWidth: 0,
                                flexDirection: 'column',
                                justifyContent: 'center',
                                pl: 1.5
                            }}
                        >
                            {pending.length === 0 ? (
                                <Typography variant="caption" color="text.secondary">
                                    {t('userPage.overview.questionnaires.noPending')}
                                </Typography>
                            ) : (
                                <>
                                    {pending.map((item) => (
                                        <Typography
                                            key={item.uuid}
                                            variant="caption"
                                            noWrap
                                            title={item.questionnaire?.name || ''}
                                            sx={{
                                                color: 'text.secondary',
                                                fontWeight: 600,
                                                lineHeight: 1.35
                                            }}
                                        >
                                            {item.questionnaire?.name || '—'}
                                        </Typography>
                                    ))}
                                    {remainingPending > 0 && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: '#263238',
                                                fontWeight: 700,
                                                lineHeight: 1.35
                                            }}
                                        >
                                            {t('userPage.overview.questionnaires.more', {
                                                count: remainingPending
                                            })}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
            )}

            <Button
                size="small"
                endIcon={<ArrowForwardIcon fontSize="small" />}
                onClick={onViewQuestionnaires}
                sx={{
                    alignSelf: 'flex-end',
                    mt: 'auto',
                    mr: -4,
                    px: 0.5,
                    fontWeight: 600
                }}
            >
                {t('userPage.overview.viewQuestionnaires')}
            </Button>
        </OverviewCard>
    );
};

QuestionnairesOverviewCard.propTypes = {
    patientUuid: PropTypes.string.isRequired,
    onViewQuestionnaires: PropTypes.func.isRequired
};

export default QuestionnairesOverviewCard;
